import { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Play, FileDown, FlaskConical, ShieldOff, Move, GitBranch, Lock } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  non_compliant_device: ShieldOff,
  lateral_movement: Move,
  vpn_vs_ztna: GitBranch,
  least_privilege: Lock,
};

export default function ScenariosPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [scenarios, setScenarios] = useState([]);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const isSim = user?.mode === "simulated";

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/scenarios"),
      api.get("/scenarios/runs"),
    ]);
    setScenarios(s.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, [user?.mode]);

  const run = async (id) => {
    if (!isSim) {
      toast.error("Les scénarios s'exécutent uniquement en Mode Simulé");
      return;
    }
    setRunning(id);
    try {
      const { data } = await api.post(`/scenarios/${id}/run`);
      setLastRun(data);
      toast.success(`Scénario ${data.name} — ${data.outcome.toUpperCase()}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRunning(null);
    }
  };

  const downloadReport = async (runId) => {
    const token = localStorage.getItem("dndi_token");
    const res = await fetch(`${API}/scenarios/runs/${runId}/report.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) { toast.error("Erreur téléchargement"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `scenario_${runId}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="scenarios-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
            {t("scenarios")}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Rejeu de validation</h1>
          <p className="mt-1 text-sm text-white/50">{t("scenarios_intro")}</p>
        </div>
      </div>

      {!isSim && (
        <Card className="bg-orange-500/10 border-orange-500/30 rounded-sm p-4 text-sm text-orange-200"
              data-testid="simulated-only-warning">
          {t("simulated_only")}. Passez en Mode Simulé pour lancer les scénarios.
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scenarios.map((s) => {
          const Icon = ICONS[s.id] || FlaskConical;
          return (
            <Card key={s.id} className="bg-white/[0.02] border-white/10 rounded-sm p-4 flex flex-col"
                  data-testid={`scenario-${s.id}`}>
              <div className="flex items-start justify-between">
                <Icon className="h-6 w-6 text-cyan-400" />
                <StatusBadge status="info">{s.id}</StatusBadge>
              </div>
              <div className="mt-3 text-lg font-semibold tracking-tight">{s.name}</div>
              <div className="mt-1 text-xs text-white/60 flex-1">{s.objective}</div>
              <Button
                disabled={!isSim || running === s.id}
                data-testid={`run-${s.id}`}
                onClick={() => run(s.id)}
                className="mt-4 bg-cyan-500 text-black hover:bg-cyan-400 self-start"
              >
                <Play className="h-4 w-4" /> {running === s.id ? "..." : t("run")}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Live result of last run */}
      {lastRun && (
        <Card className="bg-white/[0.02] border-cyan-500/30 rounded-sm p-4" data-testid="last-run-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-400">
                Dernier scénario exécuté
              </div>
              <div className="text-lg font-semibold tracking-tight">{lastRun.name}</div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={lastRun.outcome}>{lastRun.outcome}</StatusBadge>
              <Button size="sm" onClick={() => downloadReport(lastRun._id)}
                      data-testid="download-last-report"
                      className="bg-white/10 hover:bg-white/20 text-white">
                <FileDown className="h-4 w-4" /> {t("download_pdf")}
              </Button>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {lastRun.steps.map((s, i) => (
              <div key={i} className="border border-white/10 rounded-sm p-2 flex items-start gap-3">
                <div className="text-[10px] font-mono text-white/40 mt-0.5">{i + 1}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{s.label}</span>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xs text-white/60 mt-1">{s.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-white/70">{lastRun.conclusion}</div>
        </Card>
      )}

      {/* Past runs */}
      {runs.length > 0 && (
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
            Historique ({runs.length})
          </div>
          <div className="space-y-2">
            {runs.map((r) => (
              <div key={r._id}
                   className="flex items-center justify-between border border-white/10 rounded-sm p-3 bg-white/[0.02]">
                <div>
                  <div className="text-sm">{r.name}</div>
                  <div className="text-[10px] font-mono text-white/40">
                    {new Date(r.timestamp).toLocaleString()} · {r.run_by}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.outcome}>{r.outcome}</StatusBadge>
                  <Button size="sm" variant="ghost" onClick={() => downloadReport(r._id)}
                          data-testid={`download-${r._id}`}
                          className="text-white/70 hover:text-cyan-400 hover:bg-white/5">
                    <FileDown className="h-4 w-4" /> PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
